/**
 * Growing the world (docs/DESIGN_GROWTH.md): four shifts from `content/jobs` (kind `work`)
 * that put plans into the overlay (world/overlay.ts) and build them.
 *
 *  plan  — city planner: from the town hall, walk beyond the city edge; J lists street pieces to
 *          stake on the 16-tile lattice (they must touch a road) and zones for the block you stand in.
 *  pave  — road crew: a paver; stop it beside the next unpaved tile of a planned piece and it lays
 *          asphalt tile by tile; refill the hopper at a yard.
 *  build — construction crew: a truck of materials; park beside a construction site (a lot on a
 *          paved piece in a zoned block) and it delivers unit by unit until the building stands.
 *  plant — parks crew: a pickup of plants; on foot, J puts a tree / flower bed / bench on the tile.
 *
 * Every unit of work is paid straight from the city budget (the overlay's `budget`) — an empty
 * budget means no pay until midnight's taxes. Nothing here runs client-side: the rows come over
 * the wire (YouSticky.wk) and the chosen row's id comes back.
 */
import { count as countAch } from './achievements';
import { customers, stepMechanicShift } from './mechanic';
import { endPoliceShift, policeObjective, startDuty, stepPoliceShift } from './patrol'; // (Sep 25) the police shift
import { vehicleMaxHp } from './shop';
import type { ZoneName } from '../content/types';
import { fits, type PlacedBuilding } from '../world/buildings';
import type { Lot } from '../world/lots';
import { Overlay, PIECE, cellKey, type RoadPiece } from '../world/overlay';
import type { Axis } from '../world/roads';
import type { Game, PlayerState } from './game';
import { plotPrice } from '../world/custom';
import type { ActiveJob, JobPlace } from './jobs';
import type { ShopRow } from './shop';
import { radioPick, radioRows } from './radio';
import { ZONE_CHOICES, nearestPlannable, pieceGaps, piecesAround, planCheck as planCheckAt, zoneCheck as zoneCheckAt } from './planning';

export type WorkKind = 'plan' | 'pave' | 'build' | 'plant' | 'radio' | 'mechanic' | /** (Sep 25) src/game/patrol.ts */ 'police';
export interface WorkState { kind: WorkKind; /** what the rig carries (tiles of asphalt, units of material, plants) and its capacity */ load: number; cap: number; /** units done and $ earned this shift */ units: number; earned: number; vid?: number; /** seconds working on the current tile / unit */ at: number; /** told about the empty hopper */ told?: boolean; /** (owner, Sep 16) mechanic: the shop lent the toolbox */ lent?: boolean }

export const CAP: Record<WorkKind, number> = { plan: 0, pave: 48, build: 40, plant: 12, radio: 0, mechanic: 0, police: 0 };
const PAVE_S = 1.2, BUILD_S = 1.5, REACH = 9, REFILL_R = 14;
export const PLANT_CHOICES: { id: string; name: string }[] = [{ id: 'park_tree_big', name: 'a tree' }, { id: 'street_tree', name: 'a young tree' }, { id: 'flower_bed', name: 'a flower bed' }, { id: 'bench', name: 'a bench' }];

/** Units a site needs: a quarter of its footprint, at least 6 (a house ~12, a big block up to 60). */
export function siteNeed(s: PlacedBuilding): number { return Math.max(6, Math.ceil(s.w * s.h / 4)); }

const pieceRect = (r: RoadPiece): [number, number, number, number] => Overlay.pieceRect(r);
function distToRect(x: number, y: number, x0: number, y0: number, x1: number, y1: number): number { const dx = Math.max(x0 - x, 0, x - x1), dy = Math.max(y0 - y, 0, y - y1); return Math.hypot(dx, dy); }

// ---- planning ---- (the geometry: game/planning.ts, shared with the client's strip and map layer)
/** Why a piece cannot be planned here, or '' when it can. */
export function planCheck(game: Game, axis: Axis, pos: number, f: number): string { return planCheckAt(game.world, axis, pos, f); }
function zoneCheck(game: Game, x: number, y: number): string { return zoneCheckAt(game.world, x, y); }
export { piecesAround, ZONE_CHOICES };
/** (owner, Sep 18) the nearest piece a planner can stake, found for the objective at most every few seconds and again when they walked a block */
const planHints = new WeakMap<WorkState, { at: number; px: number; py: number; x: number; y: number; none: boolean }>();
function planHint(game: Game, w: WorkState, p: { x: number; y: number }): { x: number; y: number; none: boolean } {
  const now = Date.now(); let h = planHints.get(w);
  if (!h || now - h.at > 4000 || Math.hypot(h.px - p.x, h.py - p.y) > PIECE) { const n = nearestPlannable(game.world, p.x, p.y); h = { at: now, px: p.x, py: p.y, x: n?.x ?? p.x, y: n?.y ?? p.y, none: !n }; planHints.set(w, h); }
  return h;
}

/** The J list during a shift. */
export function workRows(game: Game, st: PlayerState): ShopRow[] {
  const j = st.job; const w = j?.work; if (!j || !w) return [];
  const p = st.person, def = game.reg.get('job', j.id), pay = def?.pay.perTile ?? 0;
  if (w.kind === 'radio') return radioRows(game, st);
  const rows: ShopRow[] = [];
  const ov = game.world.overlay;
  if (j.stage === 'pickup') rows.push({ kind: 'work', id: 'none', name: w.kind === 'police' ? (w.vid !== undefined ? 'Get in your cruiser first' : `Report to ${j.from.name} first`) : w.vid !== undefined ? 'Get in the rig first' : `Report to ${j.from.name} first`, price: 0 });
  else if (w.kind === 'police') { const o = policeObjective(game, j, p); rows.push({ kind: 'work', id: 'none', name: o.label, price: 0 }); }
  else if (w.kind === 'plan') {
    if (p.inside) rows.push({ kind: 'work', id: 'none', name: 'Step outside to plan', price: 0 });
    else {
      for (const c of piecesAround(p.x, p.y)) {
        const why = planCheck(game, c.axis, c.pos, c.f);
        const dir = c.axis === 'v' ? 'north–south' : 'east–west';
        const cur = ov.piece(c.axis, c.pos, c.f);
        if (cur) rows.push(cur.b > 0 ? { kind: 'work', id: 'none', name: `Street ${dir}: ${cur.b}/${PIECE} paved`, price: 0 } : { kind: 'work', id: `unplan:${c.axis}:${c.pos}:${c.f}`, name: `Clear the planned street ${dir}`, price: 0 });
        else if (!why) rows.push({ kind: 'work', id: `plan:${c.axis}:${c.pos}:${c.f}`, name: `Plan a street ${dir} here`, price: pay });
        else if (why !== 'a road is already here') rows.push({ kind: 'work', id: 'none', name: `Street ${dir}: ${why}`, price: 0 });
      }
      // Q46: standing on a lot that is still a site (nothing delivered yet): pick its building from the zone's list, or leave it to the generator
      const site = siteUnder(game, p.x, p.y);
      if (site) {
        const lot = lotOf(game, site.key);
        if (lot) {
          const picked = ov.picks.get(site.key);
          rows.push({ kind: 'work', id: picked ? `pickb:${site.key}:auto` : 'none', name: picked ? `Building here: ${site.def.name} (chosen) — back to auto` : `Building here: ${site.def.name} (auto)`, price: 0 });
          const cands = game.reg.forZone('building', lot.zone).filter((b) => fits(b, lot) && b.id !== site.def.id).sort((a, b) => a.name.localeCompare(b.name));
          for (const b of cands) rows.push({ kind: 'work', id: `pickb:${site.key}:${b.id}`, name: `Put here: ${b.name}`, price: 0 });
        }
      }
      const zwhy = zoneCheck(game, p.x, p.y);
      const cur = ov.zoneAt(p.x, p.y);
      if (zwhy) rows.push({ kind: 'work', id: 'none', name: `Zoning: ${zwhy}`, price: 0 });
      else {
        for (const z of ZONE_CHOICES) if (z !== cur) rows.push({ kind: 'work', id: `zone:${z}`, name: `Zone this block: ${game.reg.get('zone', z)?.name ?? z}`, price: pay });
        if (cur) rows.push({ kind: 'work', id: 'unzone', name: `Clear the zoning (${game.reg.get('zone', cur)?.name ?? cur})`, price: 0 });
      }
    }
  } else if (w.kind === 'plant') {
    const inRig = p.vehicle && (p.vehicle.id === w.vid || p.vehicle.def.id === def?.vehicle);
    if (inRig) rows.push({ kind: 'work', id: 'none', name: 'Get out on the tile to plant', price: 0 });
    else if (w.load <= 0) rows.push({ kind: 'work', id: 'none', name: 'Nothing left to plant — refill the pickup at a garden', price: 0 });
    else {
      const why = plantCheck(game, p.x, p.y);
      if (why) rows.push({ kind: 'work', id: 'none', name: `Not here: ${why}`, price: 0 });
      else for (const c of PLANT_CHOICES) if (game.reg.has('prop', c.id)) rows.push({ kind: 'work', id: `plant:${c.id}`, name: `Plant ${c.name} here`, price: pay });
    }
  } else if (w.kind === 'mechanic') {
    const cars = customers(game, st);
    if (!cars.length) rows.push({ kind: 'work', id: 'none', name: 'Waiting for customers — stay in the yard', price: 0 });
    for (const v of cars) rows.push({ kind: 'work', id: 'none', name: `${v.customerDone !== undefined ? 'Done: ' : ''}${v.def.name} — ${Math.round((v.hp / vehicleMaxHp(v)) * 100)} %`, price: 0 });
  } else rows.push({ kind: 'work', id: 'none', name: w.kind === 'pave' ? `Asphalt: ${w.load}/${w.cap} tiles — stop beside a planned street` : `Materials: ${w.load}/${w.cap} — park beside a construction site`, price: 0 });
  rows.push({ kind: 'work', id: 'end', name: w.kind === 'police' ? `End the shift (${w.units} call${w.units === 1 ? '' : 's'}, $${w.earned}${w.units > 0 ? ` + $${def?.pay.base ?? 0} for the shift` : ''})` : w.kind === 'mechanic' ? `Clock out ($${w.earned} earned, ${w.units} fixed)` : `End the shift ($${w.earned} earned · city budget $${Math.floor(ov.budget)})`, price: 0 });
  return rows.slice(0, 12);
}

/** Why nothing can be planted on this tile, or ''. */
export function plantCheck(game: Game, x: number, y: number): string {
  const tx = Math.floor(x), ty = Math.floor(y), w = game.world, ov = w.overlay;
  if (!w.loaded(tx, ty)) return 'unloaded';
  if (w.roadAt(tx, ty)) return 'on the road';
  if (w.buildingAt(tx, ty) || w.blockH(tx, ty) > 0 || w.decorH(tx, ty) > 0) return 'something stands here';
  const t = w.terrainAt(tx, ty); if (!t || !t.buildable) return 'not on water';
  if (ov.plantAt(tx, ty)) return 'already planted';
  if (!ov.zoneAt(tx, ty) && !ov.piecesIn(tx - 8, ty - 8, tx + 9, ty + 9).length) return 'only in zoned blocks or by planned streets';
  if (w.sitesIn(tx, ty, tx + 1, ty + 1).length) return 'a construction site';
  return '';
}

/** Pay a unit of work from the city budget; tells the player when the city is broke (once a minute). */
function payWork(game: Game, st: PlayerState, amount: number): number {
  const w = st.job!.work!;
  const paid = game.world.overlay.pay(amount);
  w.earned += paid; w.units++;
  if (paid > 0) { st.person.items.cash = (st.person.items.cash ?? 0) + paid; countAch(game, st, 'earned', paid); }
  else if (game.time - (st.brokeAt ?? -1e9) > 60) { st.brokeAt = game.time; game.say('The city budget is empty — no pay until the midnight taxes come in', st.person); }
  return paid;
}

/** The construction site (nothing delivered yet) whose lot the point is on. */
export function siteUnder(game: { world: Game['world'] }, x: number, y: number): PlacedBuilding | undefined {
  for (const s of game.world.sitesIn(x - 20, y - 20, x + 20, y + 20)) { const lot = lotOf(game, s.key); if (lot && x >= lot.x && x < lot.x + lot.w && y >= lot.y && y < lot.y + lot.h && game.world.overlay.progress(s.key) <= 0) return s; }
  return undefined;
}
export function lotOf(game: { world: Game['world'] }, key: string): Lot | undefined { for (const lc of game.world.liveChunks()) { const l = lc.chunk.lots.find((x) => x.key === key); if (l) return l; } return undefined; }
/** (owner, Sep 22) the plot under a spot: a bare site on a player-zoned lot (world/custom.ts) — for the rows outdoors and the Build app */
export function plotAt(game: { world: Game['world'] }, x: number, y: number): { key: string; lot: Lot; site?: PlacedBuilding } | undefined {
  const site = siteUnder(game, x, y); const lot = site ? lotOf(game, site.key) : undefined; if (site && lot) return { key: site.key, lot, site };
  const v = vacantUnder(game, x, y); return v ? { key: v.key, lot: v } : undefined;
}
/** (owner, Sep 22) the building the player is in or stands beside (within two tiles of its walls) — the Build app's re-modelling and demolition */
export function buildingNear(game: { world: Game['world'] }, x: number, y: number): PlacedBuilding | undefined {
  const at = game.world.buildingAt(x, y); if (at) return at;
  for (const lc of game.world.liveChunks()) for (const b of lc.chunk.buildings) if (x >= b.x - 2 && x < b.x + b.w + 2 && y >= b.y - 2 && y < b.y + b.h + 2) return b;
  return undefined;
}
/** (owner, Sep 22) the rows on a plot, the same on the server and the client (the list is picked by index — the client must show what the server has): buy it, or design on yours */
export function plotRowsOf(at: { key: string; lot: Lot }, plot: { owner: string; name: string } | undefined, mine: boolean, designed: boolean): ShopRow[] {
  if (!plot) return [{ kind: 'buyplot', id: at.key, name: `Buy this plot — ${at.lot.front} m along the street, ${at.lot.depth} m deep`, price: plotPrice(at.lot.w, at.lot.h, at.lot.zone) }];
  if (mine && !designed) return [{ kind: 'buildhere', id: at.key, name: 'Your plot — design a building in the Build app (🏗)', price: 0 }];
  return [];
}
/** (owner, Sep 22) the vacant lot under a point: bare ground of a town the generator left, a plot for sale (world/buildings.ts `vacantLot`) */
export function vacantUnder(game: { world: Game['world'] }, x: number, y: number): Lot | undefined { for (const lc of game.world.liveChunks()) { const l = lc.chunk.lots.find((q) => q.vacant && x >= q.x && x < q.x + q.w && y >= q.y && y < q.y + q.h); if (l) return l; } return undefined; }

/** A chosen row of the work list. */
export function pickWork(game: Game, st: PlayerState, row: ShopRow): void {
  const j = st.job; const w = j?.work; if (!j || !w || !row.id || row.id === 'none') return;
  const p = st.person, ov = game.world.overlay, def = game.reg.get('job', j.id), pay = def?.pay.perTile ?? 0;
  if (row.id === 'end') { endShift(game, st); return; }
  if (w.kind === 'radio') { radioPick(game, st, row.id); return; }
  const parts = row.id.split(':');
  if (parts[0] === 'plan' || parts[0] === 'unplan') {
    const axis = parts[1] as Axis, pos = Number(parts[2]), f = Number(parts[3]);
    { const cur = ov.piece(axis, pos, f); if (cur?.by && cur.by !== st.token) { game.say(`${cur.byName ?? 'Another planner'} staked that street — only their shift can change it`, p); return; } } // Q44
    if (parts[0] === 'unplan') { if (ov.unplanRoad(axis, pos, f)) game.say('Plan cleared', p); return; }
    const why = planCheck(game, axis, pos, f); if (why) { game.say(`Cannot plan that: ${why}`, p); return; }
    const road = game.reg.has('road', 'street') ? 'street' : game.reg.all('road').find((r) => r.class === 'street' || r.class === 'lane')?.id; if (!road) return;
    const piece = ov.planRoad(axis, pos, f, road, { token: st.token, name: st.name }, pieceGaps(game.world, axis, pos, f)); if (!piece) return; // (the path world: the gap to the kerb is paved with the piece)
    game.world.overlayChanged(...pieceRect(piece), false);
    const paid = payWork(game, st, pay);
    game.say(`Street staked out ${axis === 'v' ? 'north–south' : 'east–west'} — a road crew can pave it${paid ? ` (+$${paid})` : ''}`, p);
    game.events.push({ kind: 'click', x: p.x, y: p.y });
    return;
  }
  if (parts[0] === 'pickb') {
    const key = parts[1], id = parts[2];
    const site = game.world.sitesIn(p.x - 40, p.y - 40, p.x + 40, p.y + 40).find((s) => s.key === key); if (!site || ov.progress(key) > 0) { game.say('Too late — the crew has started', p); return; }
    const lot = lotOf(game, key); if (!lot) return;
    if (id !== 'auto' && !(game.reg.get('building', id) && fits(game.reg.get('building', id)!, lot))) return;
    if (!ov.setPick(key, id === 'auto' ? undefined : id)) return;
    game.world.overlayChanged(site.x - 2, site.y - 2, site.x + site.w + 2, site.y + site.h + 2);
    game.say(id === 'auto' ? 'The generator picks the building here again' : `${game.reg.get('building', id)!.name} goes here — a construction crew builds it`, p);
    game.events.push({ kind: 'click', x: p.x, y: p.y });
    return;
  }
  if (parts[0] === 'zone' || parts[0] === 'unzone') {
    const why = zoneCheck(game, p.x, p.y); if (why) { game.say(`Cannot zone here: ${why}`, p); return; }
    const [cx, cy] = Overlay.cellOf(p.x, p.y);
    const z = parts[0] === 'zone' ? (parts[1] as ZoneName) : undefined;
    if (z && !ZONE_CHOICES.includes(z)) return;
    const curCell = ov.zones.get(cellKey(cx, cy)), cur = curCell?.z;
    if (curCell?.by && curCell.by !== st.token) { game.say(`${curCell.byName ?? 'Another planner'} zoned this block — only their shift can change it`, p); return; } // Q44
    // Q47: re-zoning a block the players built: its lots go back to bare sites (the crews rebuild for the new zone); generated blocks are never touched (zoneCheck)
    let cleared = 0;
    if (cur !== undefined && cur !== z) { for (const lc of game.world.liveChunks()) for (const l of lc.chunk.lots) { if (!l.planned || l.x + l.w <= cx * PIECE || l.x >= cx * PIECE + PIECE || l.y + l.h <= cy * PIECE || l.y >= cy * PIECE + PIECE) continue; if (ov.sites.has(l.key) || ov.picks.has(l.key)) cleared++; ov.sites.delete(l.key); ov.picks.delete(l.key); } }
    if (!ov.setZone(cx, cy, z, { token: st.token, name: st.name })) return;
    if (cleared) game.say(`${cleared} building${cleared === 1 ? '' : 's'} cleared for the new zoning`, p);
    game.world.overlayChanged(cx * PIECE - 4, cy * PIECE - 4, cx * PIECE + PIECE + 4, cy * PIECE + PIECE + 4);
    if (z) { const paid = payWork(game, st, pay); game.say(`Block zoned ${game.reg.get('zone', z)?.name ?? z} — lots appear once a street beside it is paved${paid ? ` (+$${paid})` : ''}`, p); }
    else game.say('Zoning cleared', p);
    game.events.push({ kind: 'click', x: p.x, y: p.y });
    return;
  }
  if (parts[0] === 'plant') {
    if (w.load <= 0) return;
    const why = plantCheck(game, p.x, p.y); if (why) { game.say(`Not here: ${why}`, p); return; }
    const prop = parts[1]; if (!game.reg.has('prop', prop) || !PLANT_CHOICES.some((c) => c.id === prop)) return;
    const tx = Math.floor(p.x), ty = Math.floor(p.y);
    if (!ov.plant(tx, ty, prop)) return;
    game.world.applyPlant(tx, ty, prop);
    game.world.overlayChanged(tx, ty, tx + 1, ty + 1, false);
    w.load--;
    const paid = payWork(game, st, pay);
    game.say(`${game.reg.get('prop', prop)?.name ?? 'Planted'} in — ${w.load} left${paid ? ` (+$${paid})` : ''}`, p);
    game.events.push({ kind: 'click', x: tx + 0.5, y: ty + 0.5 });
    // step off it: the tile blocks now
    const [dx, dy] = [p.x - (tx + 0.5), p.y - (ty + 0.5)]; const d = Math.hypot(dx, dy) || 1; p.x = tx + 0.5 + (dx / d) * 0.9; p.y = ty + 0.5 + (dy / d) * 0.9;
  }
}

/** Is the player stopped at the wheel of a rig of the shift's kind? (any paver will do, not just the one handed out) */
function inRig(game: Game, st: PlayerState): { x: number; y: number } | undefined {
  const p = st.person, v = p.vehicle, w = st.job?.work;
  const vdef = st.job ? game.reg.get('job', st.job.id)?.vehicle : undefined;
  if (!v || !w || v.driver !== p || (v.id !== w.vid && v.def.id !== vdef) || v.wrecked || Math.abs(v.speed) > 0.8) return undefined;
  return v;
}
/** Nearest unfinished planned piece to (x, y): its next tile and distance. */
export function nextPiece(game: Game, x: number, y: number): { piece: RoadPiece; tile: { x: number; y: number }; d: number } | undefined {
  let best: { piece: RoadPiece; tile: { x: number; y: number }; d: number } | undefined;
  for (const r of game.world.overlay.roads.values()) {
    const tile = Overlay.nextTile(r); if (!tile) continue;
    const d = Math.hypot(tile.x - x, tile.y - y);
    if (!best || d < best.d) best = { piece: r, tile, d };
  }
  return best;
}
/**
 * (owner, Sep 16: "road worker shift was available although there was no roads planned yet") Is there anything for this
 * shift to do around here? A paver needs a planned street with tiles still unpaved within a drive (800 tiles), a builder a
 * site that is not finished, a planter a zoned block or a planned street to plant beside. Planning, the studio and the
 * garage always have work, so they are never held back.
 */
export function workAvailable(game: Game, at: { x: number; y: number }, kind: WorkKind): boolean {
  if (kind === 'pave') { const n = nextPiece(game, at.x, at.y); return !!n && n.d < 800; }
  if (kind === 'build') return !!nextSite(game, at.x, at.y);
  if (kind === 'plant') return game.world.overlay.zones.size > 0 || game.world.overlay.roads.size > 0;
  return true;
}

/** Nearest unfinished site in live chunks. */
export function nextSite(game: Game, x: number, y: number): { site: PlacedBuilding; d: number } | undefined {
  let best: { site: PlacedBuilding; d: number } | undefined;
  for (const s of game.world.sitesIn(x - 400, y - 400, x + 400, y + 400)) {
    if (s.main || game.world.overlay.progress(s.key) >= 1) continue; // (a wing rises with its house: the crews deliver to the main block)
    const d = distToRect(x, y, s.x, s.y, s.x + s.w, s.y + s.h);
    if (!best || d < best.d) best = { site: s, d };
  }
  return best;
}
function yardNear(game: Game, x: number, y: number, tags: string[]): PlacedBuilding | undefined {
  for (const lc of game.world.liveChunks()) for (const b of lc.chunk.buildings) if (b.height > 0 && !b.ruined && b.def.tags.some((t) => tags.includes(t)) && distToRect(x, y, b.x, b.y, b.x + b.w, b.y + b.h) <= REFILL_R) return b;
  return undefined;
}

/** Per-step progress of a shift (called from jobs.stepJob for kind 'work'). */
export function stepWork(game: Game, st: PlayerState, dt: number): void {
  const j = st.job!, p = st.person;
  const def = game.reg.get('job', j.id); if (!def) { st.job = undefined; return; }
  const w = (j.work ??= { kind: def.work ?? 'plan', load: CAP[def.work ?? 'plan'], cap: CAP[def.work ?? 'plan'], units: 0, earned: 0, at: 0 });
  if (game.time > j.deadline) { game.say('Shift over — clock out', p); endShift(game, st); return; }
  const vdef = def.vehicle ? game.reg.get('vehicle', def.vehicle) : undefined;
  if (j.stage === 'pickup') {
    const d = Math.hypot(p.x - j.from.x, p.y - j.from.y);
    if (vdef) {
      if (w.vid === undefined) { if (d < 12) { const n0 = game.vehicles.length; game.spawnVehicleNearPlayer(p, vdef.id); const nv = game.vehicles.length > n0 ? game.vehicles[game.vehicles.length - 1] : undefined; if (nv) { nv.home = w.kind === 'police' ? 'duty' : 'lost'; /* (Sep 25) a cruiser on duty is kept while you run after a burglar: a 'lost' car goes 45 m from anybody */ nv.fuel = 1; w.vid = nv.id; game.say(`Your ${vdef.name.toLowerCase()} is outside — get in${w.kind === 'pave' ? ' and find a planned street (the marker)' : w.kind === 'build' ? ' and find a construction site' : ''}`, p); } } return; }
      if ((p.vehicle?.id === w.vid || p.vehicle?.def.id === vdef.id) && p.vehicle.driver === p && w.kind === 'police') { j.stage = 'dropoff'; startDuty(game, st, j); return; }
      if ((p.vehicle?.id === w.vid || p.vehicle?.def.id === vdef.id) && p.vehicle.driver === p) { j.stage = 'dropoff'; game.say(w.kind === 'pave' ? 'On shift — stop the paver beside the next unpaved tile of a planned street' : w.kind === 'build' ? 'On shift — park beside a construction site and the crew unloads' : 'On shift — drive out to the new blocks, get out and J to plant', p); }
      return;
    }
    if (d < 12) {
      j.stage = 'dropoff';
      if (w.kind === 'mechanic') { if ((p.items.toolbox ?? 0) <= 0) { p.items.toolbox = 1; w.lent = true; } game.say(`On shift — customers pull into the yard; F beside a dented car with the toolbox${w.lent ? ' (the shop lent you one)' : ''} and stay with it`, p); }
      else game.say(w.kind === 'radio' ? 'On shift — inside the studio, J is the desk: record, mix, go on air' : 'On shift — head beyond the city edge; J lists what you can plan where you stand', p);
    }
    return;
  }
  if (w.kind === 'radio') return;
  if (w.kind === 'mechanic') { stepMechanicShift(game, st, j, dt); return; }
  if (w.kind === 'police') { stepPoliceShift(game, st, j, dt); return; }
  if (w.vid !== undefined && p.vehicle?.def.id !== vdef?.id) { const rig = game.vehicles.find((v) => v.id === w.vid); if (!rig || rig.wrecked) { game.say('The rig is a wreck — shift over', p); endShift(game, st); return; } }
  const rig = inRig(game, st);
  // refill: stopped in the rig beside any yard that hands the job out
  if (rig && w.cap > 0 && w.load < w.cap && yardNear(game, rig.x, rig.y, def.pickup)) { w.load = w.cap; w.told = false; game.say(`${w.kind === 'pave' ? 'Hopper' : w.kind === 'build' ? 'Truck' : 'Pickup'} refilled (${w.cap})`, p); }
  if (w.kind === 'pave' && rig) {
    const n = nextPiece(game, rig.x, rig.y);
    if (!n || n.d > REACH) { w.at = 0; return; }
    if (w.load <= 0) { if (!w.told) { w.told = true; game.say('Hopper empty — refill at a yard', p); } return; }
    if (!game.world.loaded(n.tile.x, n.tile.y)) return;
    w.at += dt;
    if (w.at < PAVE_S) return;
    w.at = 0; w.load--;
    const done = game.world.overlay.pave(n.piece);
    const paid = payWork(game, st, def.pay.perTile);
    if (done) { game.world.overlayChanged(...pieceRect(n.piece)); game.say(`Street finished${paid ? ` (+$${paid})` : ''} — ${nextPiece(game, rig.x, rig.y) ? 'next one on the marker' : 'no more planned streets'}`, p); game.events.push({ kind: 'click', x: n.tile.x, y: n.tile.y }); }
    else game.world.overlayChanged(...pieceRect(n.piece), false);
    return;
  }
  if (w.kind === 'build' && rig) {
    const n = nextSite(game, rig.x, rig.y);
    if (!n || n.d > REACH + 2) { w.at = 0; return; }
    if (w.load <= 0) { if (!w.told) { w.told = true; game.say('Nothing left to unload — refill at a yard', p); } return; }
    w.at += dt;
    if (w.at < BUILD_S) return;
    w.at = 0; w.load--;
    const s = n.site, need = siteNeed(s), ov = game.world.overlay;
    const before = ov.progress(s.key);
    const done = ov.build(s.key, 1 / need);
    const paid = payWork(game, st, def.pay.perTile);
    const rect: [number, number, number, number] = [s.x, s.y, s.x + s.w, s.y + s.h];
    if (done) { game.world.overlayChanged(...rect); game.say(`${s.def.name} finished${paid ? ` (+$${paid})` : ''}`, p); game.events.push({ kind: 'click', x: s.x + s.w / 2, y: s.y + s.h / 2 }); }
    else if (before <= 0) { game.world.overlayChanged(...rect); game.say(`Ground broken for a ${s.def.name.toLowerCase()} — ${need} units`, p); }
    else game.world.overlayChanged(...rect, false);
    return;
  }
}

/** Clock out: the rig goes back to being traffic; what was earned stays earned. */
export function endShift(game: Game, st: PlayerState): void {
  const j = st.job; if (!j?.work) return;
  const w = j.work;
  if (w.kind === 'police') { endPoliceShift(game, st); return; } // (Sep 25) its own end: the bonus, the uniform off, the cruiser back
  if (w.vid !== undefined) { const rig = game.vehicles.find((v) => v.id === w.vid); if (rig) rig.home = 'lost'; }
  j.pay = 0;
  const done = w.units > 0;
  if (w.kind === 'radio') { game.say(st.programme?.length ? 'Shift over — the unfinished programme is dropped' : 'Shift over', st.person); st.programme = undefined; st.studioTab = undefined; st.recording = false; st.job = undefined; st.jobsOpen = false; return; }
  if (w.kind === 'mechanic') {
    // (owner, Sep 16) the customers still waiting are somebody else's now; the lent toolbox goes back on the bench
    for (const v of game.vehicles) if (v.customer === st.person.id) { v.customer = undefined; v.customerDone = undefined; }
    if (w.lent && (st.person.items.toolbox ?? 0) > 0) st.person.items.toolbox = (st.person.items.toolbox ?? 1) - 1;
    st.repairing = undefined;
    game.say(`Shift over — ${w.units} car${w.units === 1 ? '' : 's'} fixed, $${w.earned} earned${w.lent ? ' (toolbox returned)' : ''}`, st.person);
    st.job = undefined; st.jobsOpen = false;
    if (done) { st.jobsDone = (st.jobsDone ?? 0) + 1; countAch(game, st, 'jobs', 1); countAch(game, st, 'jobs_work', 1); game.markIntroduced(st, j.id); }
    return;
  }
  game.say(`Shift over — ${w.units} ${w.kind === 'plan' ? 'plans' : w.kind === 'pave' ? 'tiles paved' : w.kind === 'build' ? 'units delivered' : 'planted'}, $${w.earned} earned`, st.person);
  st.job = undefined; st.jobsOpen = false;
  if (done) { st.jobsDone = (st.jobsDone ?? 0) + 1; countAch(game, st, 'jobs', 1); countAch(game, st, 'jobs_work', 1); countAch(game, st, 'grown', w.units); game.markIntroduced(st, j.id); }
}

/** What the HUD points at during a shift. */
export function workObjective(j: ActiveJob, game: Game, p: { x: number; y: number }): { x: number; y: number; label: string; ring?: number } | undefined {
  const w = j.work; if (!w) return undefined;
  if (j.stage === 'pickup') { if (w.kind === 'police' && w.vid !== undefined) { const car = game.vehicles.find((v) => v.id === w.vid); if (car) return { x: car.x, y: car.y, label: '🚓 Get in your cruiser', ring: 3 }; } return { x: j.from.x, y: j.from.y, label: w.vid !== undefined ? 'Get in the rig' : `Report to ${j.from.name}` }; }
  if (w.kind === 'police') return policeObjective(game, j, p);
  if (w.kind === 'pave') {
    if (w.load <= 0) return { x: p.x, y: p.y, label: 'Hopper empty — refill at a yard' };
    const n = nextPiece(game, p.x, p.y);
    return n ? { x: n.tile.x, y: n.tile.y, label: `Planned street · ${n.piece.b}/${PIECE} · ${w.load} tiles aboard`, ring: REACH } : { x: p.x, y: p.y, label: 'No planned streets — a planner stakes them' };
  }
  if (w.kind === 'build') {
    if (w.load <= 0) return { x: p.x, y: p.y, label: 'Truck empty — refill at a yard' };
    const n = nextSite(game, p.x, p.y);
    return n ? { x: n.site.x + n.site.w / 2, y: n.site.y + n.site.h / 2, label: `Site: ${n.site.def.name} · ${Math.round(game.world.overlay.progress(n.site.key) * 100)} % · ${w.load} units aboard`, ring: REACH + 2 } : { x: p.x, y: p.y, label: 'No sites loaded — zoned blocks on paved streets' };
  }
  if (w.kind === 'plant') return { x: p.x, y: p.y, label: `${w.load} plants aboard — J on a free tile` };
  if (w.kind === 'radio') return { x: j.from.x, y: j.from.y, label: 'Community FM — J in the studio' };
  if (w.kind === 'mechanic') {
    const cars = game.vehicles.filter((v) => v.customer !== undefined && v.customerDone === undefined && vehicleMaxHp(v) - v.hp >= 0.5).sort((a, b) => Math.hypot(a.x - p.x, a.y - p.y) - Math.hypot(b.x - p.x, b.y - p.y));
    const v = cars[0];
    return v ? { x: v.x, y: v.y, label: `Customer: ${v.def.name} · ${Math.round((v.hp / vehicleMaxHp(v)) * 100)} %`, ring: 3 } : { x: j.from.x, y: j.from.y, label: `${j.from.name} — waiting for customers` };
  }
  if (w.kind === 'plan') { const h = planHint(game, w, p); return h.none ? { x: p.x, y: p.y, label: 'Nothing to stake within 400 m — walk out of town' } : { x: h.x, y: h.y, label: 'Nearest street to stake', ring: 6 }; }
  return { x: p.x, y: p.y, label: 'Planning — J beyond the city edge' };
}

export type { JobPlace };
